// =============================================================================
// conversations/hooks/useApprovedTemplatesForStart.ts — Templates aprovados
// para o seletor de template ao INICIAR uma conversa (F43-S11, endurecido em
// F43-S48).
//
// Diferente de useConversationTemplates (F16-S19, GET /api/conversations/:id/
// templates): aqui ainda NÃO existe conversa — a caixa está fora do prazo de
// resposta livre e o vendedor está começando o contato agora.
//
// F43-S48 — INCIDENTE (vendedor Renato, Machadinho, 01/09/2026): até esta
// revisão, este hook chamava `GET /api/templates?status=approved`, gateado
// por `templates:read` (permissão de GESTÃO do catálogo — criar/editar/
// sincronizar templates). `templates:read` pertencia só ao `admin`; um
// vendedor com `livechat:message:send` (permissão de ENVIAR) recebia 403
// aqui — e o 403 não era distinguido de "zero templates aprovados", então a
// tela mostrava uma mensagem genérica errada. O detalhe grave: o canal do
// WhatsApp pessoal (WAHA) nunca consulta este endpoint, mas dependia da
// mesma modal, que não isolava a falha.
//
// Correção: usa `GET /api/conversations/templates-para-iniciar`
// (apps/api/src/modules/conversations/routes.ts), gateado pela MESMA
// permissão que abre este modal e envia a mensagem (`livechat:message:send`)
// — nunca mais `templates:read`. Já filtra para as categorias elegíveis a
// contato ativo (gate LGPD de `template-catalog.service.ts`, reusado no
// backend) — nenhum filtro de categoria duplicado aqui. Sem `language`: o
// backend resolve o idioma cadastrado no submit quando `languageCode` não é
// enviado (`start-channel.schema.ts`).
//
// Contrato lido em apps/api/src/modules/conversations/routes.ts (dentro de
// files_allowed deste slot):
//   { data: Array<{ id, name, category, variables, body_text }> }
// =============================================================================

import { useQuery } from '@tanstack/react-query';

import { ApiError, api } from '../../../lib/api';

export interface ApprovedTemplateForStart {
  readonly id: string;
  readonly name: string;
  readonly category: 'utility' | 'marketing' | 'authentication';
  readonly variables: string[];
  readonly body: string;
}

interface TemplateListApiResponse {
  readonly data: ReadonlyArray<{
    readonly id: string;
    readonly name: string;
    readonly category: 'utility' | 'marketing' | 'authentication';
    readonly variables: string[];
    readonly body_text: string;
  }>;
}

export const approvedTemplatesForStartKeys = {
  all: ['conversations', 'start', 'approved-templates'] as const,
} as const;

/**
 * useApprovedTemplatesForStart — lista templates aprovados elegíveis a
 * contato ativo, para o seletor de template do modal "Iniciar conversa".
 *
 * `enabled`: só dispara quando a tela realmente precisa (caixa escolhida
 * exige template) — evita um GET a mais em toda abertura da tela.
 *
 * `isError`/`error` são responsabilidade do chamador tratar de forma
 * explícita — este hook NUNCA engole falha (lição registrada: `useProducts`
 * do simulador engolia um 403 e o vendedor via dropdown vazio, sem
 * explicação nenhuma — não repetir aqui).
 */
export function useApprovedTemplatesForStart(opts: { enabled: boolean }) {
  return useQuery({
    queryKey: approvedTemplatesForStartKeys.all,
    queryFn: () => api.get<TemplateListApiResponse>('/api/conversations/templates-para-iniciar'),
    staleTime: 60_000,
    enabled: opts.enabled,
    // 403/404 são erros de autorização/estado — repetir não ajuda.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
        return false;
      }
      return failureCount < 2;
    },
    select: (res): ApprovedTemplateForStart[] =>
      res.data.map((t) => ({
        id: t.id,
        name: t.name,
        category: t.category,
        variables: t.variables,
        body: t.body_text,
      })),
  });
}
